import { ActionButton, Dropdown, Pivot, PivotItem, SearchBox, Stack, IStackTokens } from '@fluentui/react';
import { FC, useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';
import DefaultSpinner from './DefaultSpinner';
import { PageSizeContext } from '../../providers/PageSizeProvider';
import useSortedListItems, { SortableType, SortingProp } from '../../shared/hooks/useSortedListItems';
import Grid from './Grid';
import { HintText } from '../../shared/styled-components';
import { IBaseReport } from '../../models/reports.internal';
import { objectKeys } from '../../utils/objectUtils';
import { nameof } from '../../utils/stringUtils';
import { useNavigate } from 'react-router-dom';

const HeaderGrid = styled(Grid).attrs({ gap: '0.5rem' })`
  margin-bottom: 0.5rem;
  margin-left: 0.5rem;

  button {    
    width: 121px;    
    padding: 5px 12px;
    padding: 0.75rem;    
    border-radius: 0.5rem;
    border:none;    
    font-size:16px;
  }  

  .clicked {
    background-color: #f3f2f1;
    box-shadow : 0px 2px 6px 0px;
    font-weight: bold;
  }
  .defaultbtn {
    background-color: white;
    border:1px solid #D1D1D1;
    font-weight: normal;
  }
`;

const StyledPivot = styled(Pivot)`
  justify-self: center;

  * {
    line-height: normal;
    height: unset;
  }
 
 button {
    height: 32px;
    }
`;

const StyledGrid = styled(Grid).attrs({ gap: '0.5rem', alignItems: 'start' })`
  flex-grow: 1;
  overflow: auto;
  padding: 0.25rem;
`;

type SortingPropWithoutKey<T> = keyof T | { selector(item: T): SortableType } | { comparer(item1: T, item2: T): number };
export type SortOptions<T> = Record<string, SortingPropWithoutKey<T>>;

interface ReportComponentProps<TReport> {
  num: number;
  report: TReport;
}

export interface ReportDataState<T extends IBaseReport> {
  items: T[];
  isLoading?: boolean;
  willNotLoad?: boolean;
  willNotLoadText?: string;
}

interface IProps<T extends IBaseReport> {
  layout: 'list' | 'grid';
  reportData: ReportDataState<T>;
  reportComponent: FC<ReportComponentProps<T>>;
  tabs?: { key: string; text: string }[];
  selectedTab?: string;
  onTabChanged?(key: string): void;
  sortOptions: SortOptions<T>;
  itemsName: string;
}

const ReportsList = <T extends IBaseReport>(props: IProps<T>): JSX.Element => {
  const {
    layout,
    reportData,
    reportComponent: ReportComponent,
    tabs,
    selectedTab,
    onTabChanged,
    sortOptions,
    itemsName,
  } = props;

  const { isMediumOrLarger } = useContext(PageSizeContext);

  //#region [1] Filtering
  const [searchText, setSearchText] = useState('');

  const updateSearchText = useCallback(
    (st: string) => {
      if (st !== searchText) {
        setSearchText(st);
      }
    },
    [searchText],
  );
  const debouncedSearch = useDebouncedCallback(updateSearchText, 800);

  const filteredReports = useMemo(
    () => {
      const st = searchText.toLowerCase();
      return reportData.items.filter(r => r.name?.toLowerCase()?.includes(st));
    },
    [reportData, searchText],
  );
  //#endregion

  //#region [2] Sorting
  const extendedSortOptions = useMemo<SortOptions<T>>(
    () => ({
      'Name': { selector: (rep) => rep.name },
      ...sortOptions,
    }),
    [sortOptions],
  );

  const getSortProp = useCallback(
    (key: string): SortingProp<T> => {
      const sortProp = extendedSortOptions[key];
      if (typeof sortProp === 'object')
        return { key, ...sortProp };
      else
        return sortProp;
    },
    [extendedSortOptions],
  );

  const sortDropdownOptions = useMemo(
    () => objectKeys(extendedSortOptions).map(key => ({ key, text: key })),
    [extendedSortOptions],
  );

  const { sortedItems: sortedReports, sortingProp, isSortedDescending, sortBy } = useSortedListItems(filteredReports, getSortProp('Name'));
  //#endregion

  // Create a list key to be used for triggering css animations on sorting
  const gridKey = useMemo(
    () => {
      const sortingPropText = typeof sortingProp === 'object'
        ? sortingProp?.key ?? ''
        : sortingProp.toString();

      return `${sortingPropText}-${selectedTab ?? 'none'}-${isSortedDescending ? 'desc' : 'asc'}-${searchText}`;
    },
    [isSortedDescending, searchText, selectedTab, sortingProp],
  );

  const content = useMemo(
    () => {
      if (reportData.willNotLoad)
        return <HintText centered italic height="100%" verticalAlign="center">{reportData.willNotLoadText ?? 'No reports to show'}</HintText>;

      if (reportData.isLoading)
        return <DefaultSpinner fullHeight label="Waiting for reports ..." />;

      return (
        <StyledGrid key={gridKey} columns={{ mode: 'equal', count: layout === 'list' ? 1 : (isMediumOrLarger ? 3 : 2) }}>
          {sortedReports/*.slice(0, 1)*/.map((r, i) => <ReportComponent key={i} num={i} report={r} />)}
        </StyledGrid>
      );
    },
    [ReportComponent, gridKey, isMediumOrLarger, layout, reportData.isLoading, reportData.willNotLoad, reportData.willNotLoadText, sortedReports],
  );

 
  
  const navigateTo = useNavigate();
  const stackTokens: IStackTokens = { childrenGap: 10 };
 
  
  return <>
    <HeaderGrid flow="column" columns={{ mode: 'template', sizes: tabs ? '1fr auto 1fr' : '1fr 1fr' }} gap="0.5rem">   

      <Stack.Item align="center">
        <Stack horizontal tokens={stackTokens} horizontalAlign="start" verticalFill >        
           <button  className= {itemsName=== "teams"? "clicked" :  "defaultbtn"} onClick={() => navigateTo('/TeamsReports')} >Teams</button>
          <button className= {itemsName=== "queues"? "clicked" :  "defaultbtn"}onClick={() => navigateTo('/QueueReports')} >Queues</button>
        </Stack>
      </Stack.Item>

      <SearchBox
        styles={{ root: { width: 225, margin: 10 }}}
        placeholder="Search by name..."
        onSearch={updateSearchText}
        onClear={() => updateSearchText('')}
        onChange={(_, text) => debouncedSearch(text ?? '')}
      />
      {
        tabs &&
        <StyledPivot headersOnly selectedKey={selectedTab} onLinkClick={(item) => (item && item.props.itemKey) && onTabChanged?.(item.props.itemKey)}>
          {tabs.map(({ key, text }) => <PivotItem key={key} itemKey={key} headerText={text} />)}
        </StyledPivot>
      }
      {
        reportData.items.length > 1 &&
        <Stack horizontal verticalAlign="center" style={{ justifySelf: 'end' }}>
          <span style={{ marginRight: 8 }}>Sort {itemsName} by</span>
          <Dropdown
            defaultSelectedKey="Name"
            options={sortDropdownOptions}
            onChange={(_, option) => sortBy(option ? getSortProp(option.key as string) : getSortProp('Name'))}
            styles={{ root: { width: 200 } }}
          />
          <ActionButton iconProps={{ iconName: isSortedDescending ? 'SortDown' : 'SortUp' }} onClick={() => sortBy(sortingProp)}>
            {isSortedDescending ? 'Descending' : 'Ascending'}
          </ActionButton>
        </Stack>
      }
    </HeaderGrid>
    {content}
  </>;
};

ReportsList.displayName = nameof({ ReportsList });
export default ReportsList;